import React, { useEffect } from 'react';

import { Select } from '@aline/common';
import { routes } from '@aline/routes';
import { ButtonBase, Typography } from '@mui/material';
import clsx from 'clsx';
import { Bot } from 'lucide-react';
import { matchPath, useLocation } from 'react-router-dom';
import { AlineAvatar, AlineTooltip } from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import { makeStyles } from 'tss-react/mui';

import { useIsActiveTabFocused } from './focused-view';
import { useGlobalDrawer } from './GlobalRightDrawer';
import { NotificationBell } from './NotificationBell';
import { TabBar } from './tabs';

export const globalTopBarHeightPx = 44;

const useStyles = makeStyles()(theme => ({
  aiToggle: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 20,
    boxSizing: 'border-box',
    color: theme.appColors.iconsType,
    display: 'flex',
    gap: theme.spacing(0.5),
    height: 32,
    padding: theme.spacing(0.5, 1.5),
  },
  aiToggleActive: {
    backgroundColor: theme.appColors.selectedBackground,
    borderColor: theme.appColors.primary,
    color: theme.appColors.primary,
  },
  avatar: {
    flexShrink: 0,
  },
  topBar: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    height: globalTopBarHeightPx,
    padding: theme.spacing(0, 1.5),
  },
}));

export function GlobalTopBar(): JSX.Element {
  const { classes } = useStyles();
  const location = useLocation();
  const isFocused = useIsActiveTabFocused();
  const { closeDrawer, isOpen, openDrawer } = useGlobalDrawer();
  const isChatPage = Boolean(matchPath({ end: false, path: routes.ChatIndex }, location.pathname));

  useEffect(() => {
    if (isChatPage) closeDrawer();
  }, [isChatPage, closeDrawer]);

  return (
    <div className={classes.topBar}>
      <TabBar />
      {!isFocused && <NotificationBell />}
      {isFocused && <LoggedInAvatar />}
      {!isChatPage && !isFocused && (
        <ButtonBase
          className={clsx(classes.aiToggle, { [classes.aiToggleActive]: isOpen })}
          data-cy={Select.globalTopBar.aiChatToggle}
          onClick={isOpen ? closeDrawer : openDrawer}
        >
          <Bot size={18} strokeWidth={1.5} />
          <Typography variant="body2">AI Chat</Typography>
        </ButtonBase>
      )}
    </div>
  );
}

function LoggedInAvatar(): JSX.Element | null {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  if (!currentUser) return null;

  return (
    <AlineTooltip arrow placement="bottom" title={`Logged in as ${currentUser.email}`}>
      <AlineAvatar
        avatarInfo={currentUser}
        className={classes.avatar}
        data-cy={Select.globalTopBar.loggedInAvatar}
        styles={{ size: 'md' }}
      />
    </AlineTooltip>
  );
}
